import { AccessiblePromise } from './AccessiblePromise';
export class AbstractCache {
    data = new Map();
    head;
    tail;
    resolveNext;
    pendingUpdates = 0;
    loading = false;
    constructor(entries) {
        this.head = new Promise(resolve => {
            this.resolveNext = resolve;
        });
        this.load(entries);
    }
    merge(newValue, _oldValue) {
        return newValue;
    }
    async *[Symbol.asyncIterator]() {
        let cursor = this.head;
        while (!('then' in cursor) || this.pendingUpdates) {
            const node = await cursor;
            yield [node.key, this.serialize(node.value)];
            cursor = node.next;
        }
    }
    async load(entries) {
        if (!entries)
            return;
        this.loading = true;
        try {
            for await (const [key, value] of entries) {
                const cacheValue = this.data.get(key);
                if (cacheValue && cacheValue.resolve) {
                    cacheValue.resolve(this.deserialize(value));
                    delete cacheValue.resolve;
                }
                else {
                    this.put(key, AccessiblePromise.resolve(this.deserialize(value)));
                }
            }
            for (const cacheValue of this.data.values()) {
                if (cacheValue.resolve) {
                    cacheValue.resolve();
                    delete cacheValue.resolve;
                }
            }
        }
        finally {
            this.loading = false;
        }
    }
    get(key, supplier) {
        const serializedKey = this.serializeKey(key);
        let cacheValue = this.data.get(serializedKey);
        if (!cacheValue) {
            if (this.loading) {
                let resolve;
                const pending = AccessiblePromise.resolve(new Promise(res => {
                    resolve = (value) => {
                        res(value ?? supplier(key));
                    };
                }));
                cacheValue = { pending, resolve };
            }
            else {
                const pending = AccessiblePromise.resolve(supplier(key));
                cacheValue = { pending };
            }
            this.put(serializedKey, cacheValue.pending, cacheValue.resolve);
        }
        return cacheValue.pending;
    }
    async put(serializedKey, pending, resolve) {
        this.pendingUpdates++;
        try {
            let cacheValue = this.data.get(serializedKey);
            if (!cacheValue) {
                cacheValue = { pending, resolve };
                this.data.set(serializedKey, cacheValue);
            }
            else {
                cacheValue.pending = pending;
                cacheValue.resolve = resolve;
            }
            const oldNode = cacheValue.node;
            try {
                let value = await pending;
                if (cacheValue.pending !== pending) {
                    // we're not the latest value, do nothing
                    return;
                }
                // add the node last
                const resolveNext = this.resolveNext;
                const next = new Promise(resolve => {
                    this.resolveNext = resolve;
                });
                if (oldNode) {
                    value = this.merge(value, oldNode.value);
                }
                const node = { key: serializedKey, value, prev: this.tail, next };
                if (this.tail) {
                    this.tail.next = node;
                }
                else {
                    this.head = node;
                }
                this.tail = cacheValue.node = node;
                resolveNext(node);
            }
            catch (e) {
                if (cacheValue.pending !== pending) {
                    // we're not the latest value, do nothing
                    return;
                }
            }
            if (oldNode) {
                // remove old node
                if (oldNode.prev) {
                    oldNode.prev.next = oldNode.next;
                }
                else {
                    this.head = oldNode.next;
                }
                if ('then' in oldNode.next) {
                    if (oldNode !== this.tail)
                        throw new Error('Assertion error. LinkNode.next should be resolved unless the node is the last one');
                    this.tail = undefined;
                }
                else {
                    oldNode.next.prev = oldNode.prev;
                }
            }
        }
        finally {
            this.pendingUpdates--;
        }
    }
    static forCodec(codec) {
        return class extends AbstractCache {
            serializeKey(key) {
                return codec.stringify(key);
            }
            serialize(value) {
                return codec.stringify(value);
            }
            deserialize(value) {
                return codec.parse(value);
            }
        };
    }
}
//# sourceMappingURL=abstract-cache.js.map